<template>
<div class="py-6 px-4 sm:p-6 lg:pb-8">
        
  <h2 class="text-lg leading-6 font-medium text-gray-900">References</h2>
  <p class="mt-1 text-sm text-gray-500">
    References are used as a safeguarding measure and help us comply with legislation.
  </p>
  

  <TailwindButton
    v-if="!isAddingReference"
    @click="isAddingReference = true"
    class="mt-6"
  >
    Add Reference
  </TailwindButton>


  <div v-if="!loadingReferences && (references.length === 0 || isAddingReference)" class="bg-gray-50 overflow-hidden rounded-lg mt-6">
    <div class="px-4 py-5 sm:p-6">

      <form @submit.prevent="addReference" class="space-y-6">

        <div class="space-y-2">
          <p class="text-sm text-gray-500">
            References should be a close, trusted person who is familiar with you in a professional capacity.
            A reference <strong>cannot</strong> be a family member.
          </p>

          <p class="text-sm text-gray-500">
            We will contact the reference on your behalf to ask some simple safeguarding about you.
            We won’t share their answers with you, but you’ll see here when they have responded.
          </p>
        </div>
        
        <div class="grid grid-cols-12">
          <div class="col-span-12 sm:col-span-6">
            <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
            <input v-model.trim="newReferenceName" @input="$v.newReferenceName.$touch()" type="text" name="name" id="name" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400">
            <p v-if="$v.newReferenceName.$dirty && !$v.newReferenceName.required" class="mt-2 text-sm text-red-600">Please enter a name.</p>
          </div>
        </div>

        <div class="grid grid-cols-12">
          <div class="col-span-12 sm:col-span-6">
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <input v-model.trim="newReferenceEmail" @input="$v.newReferenceEmail.$reset()" type="email" name="email" id="email" inputmode="email" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400">
            <p v-if="$v.newReferenceEmail.$dirty && !$v.newReferenceEmail.required" class="mt-2 text-sm text-red-600">Please enter an email.</p>
            <p v-if="$v.newReferenceEmail.$dirty && !$v.newReferenceEmail.email" class="mt-2 text-sm text-red-600">Please enter a valid email.</p>
          </div>
        </div>


        <div class="mt-6">
          <div class="space-x-2">
            <TailwindButton type="submit" :loading="savingReference" :disabled="savingReference">Request Reference</TailwindButton>
          </div>

          <div v-if="$v.$anyError" class="mt-3 text-sm text-red-600">Uh oh, it looks like some errors need fixing first.</div>
        </div>
      </form>

    </div>
  </div>


  <div class="-mx-4 mt-8 overflow-hidden  ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg md:shadow">
    <table class="min-w-full divide-y divide-gray-300">
      <thead class="bg-gray-50">
        <tr>
          <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
            <span class="sm:hidden">Reference</span>
            <span class="hidden sm:inline">Name</span>
          </th>
          <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">Email</th>
          <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">Status</th>
          <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
            <span class="sr-only">Edit</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200 bg-white">
        <tr v-for="reference in references" :key="reference.Id">
          <td class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
            {{ reference.Name }}
            <dl class="font-normal lg:hidden">
              <dt class="sr-only sm:hidden">Email</dt>
              <dd class="mt-1 truncate text-gray-500 sm:hidden">{{ reference.Email }}</dd>
              <dt class="sr-only sm:hidden">Status</dt>
              <dd class="mt-1 truncate text-gray-500 sm:hidden">{{ reference.Status }}</dd>
            </dl>
          </td>
          <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">{{ reference.Email }}</td>
          <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">{{ reference.Status }}</td>
          <td class="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
            <button v-if="reference.Status !== 'Responded'" type="button" @click="deleteReference(reference)" class="text-blue-600 hover:text-blue-900">
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>


</div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'

import TailwindButton from './TailwindButton.vue'

import ReferenceServices from '@/services/ReferencesServices.js'
const refService = new ReferenceServices()

export default {
  components: { 
    TailwindButton
  },
  data() {
    return {
      references: [],
      loadingReferences: true,
      isAddingReference: false,
      savingReference: false,
      newReferenceName: null,
      newReferenceEmail: null,
    }
  },
  validations: {
    newReferenceName: { required },
    newReferenceEmail: { required, email },
  },
  methods: {
    async getReferences() {
      const response = await refService.getReferences()
      this.references = response.data.References
      this.loadingReferences = false
    },
    async addReference() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.savingReference = true

      await refService.addReference({
        name: this.newReferenceName,
        email: this.newReferenceEmail,
      })

      this.getReferences()

      this.savingReference = false
      this.isAddingReference = false
      this.newReferenceName = null
      this.newReferenceEmail = null
      this.$v.$reset()
    },
    async deleteReference(reference) {
      await refService.deleteReference(reference.Id)
      this.getReferences()
    }
  },
  mounted() {
    this.getReferences()
  }
}
</script>

<style>

</style>